import { useMutation, useQuery } from "@tanstack/react-query";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import CommonDialog from "@/components/common/common-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { MAX_CONTACT_INFO, PRIVACY_POLICIES_URL } from "@/constants/people";
import useCurrentUserManager from "@/hooks/use-current-user-manager";
import CMRepository from "@/repositories/cv/cv-repository";
import PeopleRepository from "@/repositories/people/people-repository";
import type { Person, PersonBody, TermAndCondition } from "@/repositories/people/types";
import useBoundStore from "@/store/bound-store";

interface PrivacyPolicyUpdateValidationProps {
  user: Partial<Person>;
}

export const PrivacyPolicyUpdateValidation: React.FC<PrivacyPolicyUpdateValidationProps> = ({
  user,
}) => {
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showConfirmLogoutModal, setShowConfirmLogoutModal] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [currentPolicyVersion, setCurrentPolicyVersion] = useState<number | null>(null);

  const currentUserManager = useCurrentUserManager();
  const cmRepository = new CMRepository();
  const peopleRepository = PeopleRepository.getInstance();
  const setPerson = useBoundStore((state) => state.setPerson);

  // Fetch current privacy policy version
  const { data: policyData, isLoading: isPolicyLoading } = useQuery({
    queryKey: ["privacy-policy-version"],
    queryFn: cmRepository.getPrivacyPolicies,
    enabled: !!user,
  });

  // Helper to build a safeguarded payload for updating the user
  const buildUserUpdatePayload = useCallback(
    async (
      baseUser: Partial<Person>,
      updatedTermsAndConditions: Partial<TermAndCondition>[]
    ): Promise<Partial<PersonBody>> => {
      let oidcEmail: string | undefined;
      try {
        const oidcUser = await currentUserManager?.getUser();
        oidcEmail = oidcUser?.profile?.email || undefined;
      } catch {
        // ignore the error as there's a fallback in place below
      }

      const safeFirstName = baseUser.firstName ?? "";
      let safeLastName = baseUser.lastName ?? "";
      if (!safeLastName || safeLastName.trim().length <= 1) {
        safeLastName = safeFirstName || safeLastName;
      }

      let safeBirthDate: string | null = baseUser.birthDate || null;
      if (safeBirthDate) {
        const d = new Date(safeBirthDate);
        if (Number.isNaN(d.getTime()) || d.getFullYear() < 1923) {
          safeBirthDate = null;
        }
      }

      const payload: Partial<PersonBody> = {
        // fallbacks and mappings
        country: baseUser.country || "XX",
        firstName: safeFirstName,
        lastName: safeLastName,
        username: baseUser.username || oidcEmail || null,
        birthDate: safeBirthDate,
        gender: baseUser.gender?.Name || null,
        maritalStatus: baseUser.maritalStatus?.Name || null,
        hasSomeDisability: baseUser.hasSomeDisability ?? false,
        placeId: (baseUser.placeId as string) || "",
        educationId:
          baseUser.education && baseUser.education.id != null
            ? String(baseUser.education.id)
            : null,
        studyArea:
          baseUser.studyArea && baseUser.studyArea.id != null
            ? String(baseUser.studyArea.id)
            : null,
        identification: baseUser.identification ?? null,
        contactInfoList: Array.isArray(baseUser.contactInfoList)
          ? baseUser.contactInfoList.slice(-MAX_CONTACT_INFO).map((contactInfo) => ({
              active: Boolean(contactInfo?.active ?? true),
              type: contactInfo?.type ?? "",
              value: contactInfo?.value ?? "",
              id: contactInfo?.id ?? null,
            }))
          : null,
        termsAndConditions: updatedTermsAndConditions,
      };

      return payload;
    },
    [currentUserManager]
  );

  // Mutation to update user's accepted privacy policy
  const updatePrivacyPolicyMutation = useMutation({
    mutationFn: async (version: number) => {
      const updatedTermsAndConditions = [
        ...(user.termsAndConditions || []),
        {
          name: "PRIVACY_POLICIES",
          date: new Date().toISOString(),
          version: version.toString(), // Convert to string to match TermAndCondition type
          link: PRIVACY_POLICIES_URL,
        },
      ];

      // Build safeguarded payload
      const userUpdatePayload = await buildUserUpdatePayload(user, updatedTermsAndConditions);

      // Use repository method to update user
      await peopleRepository.updatePersonalInfo(userUpdatePayload);

      // Return the updated user reflecting safeguarded payload values
      const updatedUser = {
        ...user,
        ...userUpdatePayload,
      };

      return updatedUser;
    },
    onSuccess: (updatedUser) => {
      setShowPrivacyModal(false);
      setIsChecked(false);
      // Update the person in the bound store with the new terms and conditions
      setPerson((currentPerson) => ({
        ...currentPerson,
        termsAndConditions: updatedUser.termsAndConditions,
      }));
    },
    onError: (_error) => {
      // Handle error - maybe show a toast notification
    },
  });

  // Check if modal should be shown
  useEffect(() => {
    if (!policyData || isPolicyLoading) {
      return;
    }

    const currentVersion = Number.parseInt(policyData?.data?.value || "1", 10);
    setCurrentPolicyVersion(currentVersion);

    // Get user's privacy policy terms
    const privacyTerms =
      user.termsAndConditions?.filter((term) => term.name === "PRIVACY_POLICIES") || [];

    // Sort by version and get the latest one
    const sortedTerms = privacyTerms.sort((a, b) => {
      const versionA = Number.parseInt(a.version || "0", 10);
      const versionB = Number.parseInt(b.version || "0", 10);
      return versionA - versionB;
    });
    const lastAcceptedTerm = sortedTerms[sortedTerms.length - 1];

    // Show modal if user hasn't accepted the current version
    const lastAcceptedVersion = lastAcceptedTerm?.version
      ? Number.parseInt(lastAcceptedTerm.version, 10)
      : 0;
    if (!lastAcceptedTerm || lastAcceptedVersion !== currentVersion) {
      setShowPrivacyModal(true);
    }
  }, [policyData, user, isPolicyLoading]);

  const handleAcceptPrivacyPolicy = () => {
    if (!currentPolicyVersion || !isChecked) {
      return;
    }

    updatePrivacyPolicyMutation.mutate(currentPolicyVersion);
  };

  const handleRejectPrivacyPolicy = () => {
    setShowPrivacyModal(false);
    setShowConfirmLogoutModal(true);
  };

  const handleConfirmLogout = () => {
    setIsLoggingOut(true);
    currentUserManager?.signoutRedirect();
  };

  const handleCancelLogout = () => {
    setShowConfirmLogoutModal(false);
    setShowPrivacyModal(true);
  };

  if (isPolicyLoading) {
    return null; // Or a loading spinner
  }

  return (
    <>
      {/* Privacy Policy Modal */}
      <CommonDialog
        open={showPrivacyModal}
        onOpenChange={() => {
          // Prevent closing without action - modal can only be closed via buttons
        }}
        title="POLÍTICA DE PRIVACIDAD Y PROTECCIÓN DE DATOS PERSONALES"
        description={
          <>
            La política de privacidad y protección de datos personales ha sido actualizada, para más
            información visite el siguiente enlace:{" "}
            <a
              href={PRIVACY_POLICIES_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              Política de Privacidad
            </a>
            .
            <br />
            <br />
            <span className="inline-flex items-center gap-2">
              <Checkbox
                id="privacy-checkbox"
                checked={isChecked}
                onCheckedChange={(checked) =>
                  setIsChecked(checked === "indeterminate" ? false : checked)
                }
              />
              <Label htmlFor="privacy-checkbox" className="text-sm">
                He leído y acepto la Política de Protección y Uso de Datos
              </Label>
            </span>
          </>
        }
        primaryButtonText={
          updatePrivacyPolicyMutation.isPending
            ? "Guardando..."
            : isChecked
              ? "Guardar"
              : "Debe aceptar para continuar"
        }
        secondaryButtonText="Rechazar"
        onPrimaryButtonClick={handleAcceptPrivacyPolicy}
        onSecondaryButtonClick={handleRejectPrivacyPolicy}
        loading={updatePrivacyPolicyMutation.isPending}
        onPointerDownOutside={(e) => e.preventDefault()}
        secondaryButtonDisabled={updatePrivacyPolicyMutation.isPending}
      />

      {/* Logout Confirmation Modal */}
      <CommonDialog
        open={showConfirmLogoutModal}
        onOpenChange={(open) => {
          if (!open) {
            setShowConfirmLogoutModal(false);
          }
        }}
        title="POLÍTICA DE PRIVACIDAD Y PROTECCIÓN DE DATOS PERSONALES"
        description="¿Estás seguro?, si continúas saldrás de Evaluar.com y serás redirigido a la página de inicio de sesión."
        primaryButtonText={isLoggingOut ? "Cerrando sesión..." : "Cerrar sesión"}
        secondaryButtonText="Volver"
        onPrimaryButtonClick={handleConfirmLogout}
        onSecondaryButtonClick={handleCancelLogout}
        loading={isLoggingOut}
        iconVariant="warning"
        onPointerDownOutside={(e) => e.preventDefault()}
        secondaryButtonDisabled={isLoggingOut}
      />
    </>
  );
};
